import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {Link} from 'shells/link/link';
import {RightFrame, ModalMain, FooterWithControls} from 'shells/modal/step-modal/layout';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {Body} from 'shells/typography/body';
import {ModalHeader} from 'shells/typography/modal-header';
import {ModalSubheader} from 'shells/typography/modal-subheader';
import {ModalTitle} from 'shells/typography/modal-title';

import {trackModalPageLoad} from '../track-page-load';

import {INITIAL_SYNC_NUM_DAYS_AGO} from './consts';

type Props = {
    goBack: () => void;
    nextPage: string;
    currentUrl: string;
};

export function InitialSync(props: Props) {
    const [shouldSyncOldEmails, setShouldSyncOldEmails] = React.useState<'0' | '1'>('1');
    const [shouldCreatePeople, setShouldCreatePeople] = React.useState<'0' | '1'>('1');
    const disableCreate = shouldSyncOldEmails === '0';

    React.useEffect(() => {
        trackModalPageLoad(props.currentUrl);
    }, [props.currentUrl]);

    return (
        <RightFrame>
            <ModalMain>
                <SpacerGroup mb={32}>
                    <ModalHeader>How about we give you a head start?</ModalHeader>
                    <ModalSubheader>
                        Nutshell email sync only pulls in new emails moving forward, but we can add
                        some older messages to get you started.
                    </ModalSubheader>
                </SpacerGroup>
                <SpacerGroup mb={32}>
                    <ModalTitle>
                        Should we start by syncing in some of your older emails?
                    </ModalTitle>
                    <RadioGroup
                        name='sync-old'
                        selectedValue={shouldSyncOldEmails}
                        // @ts-expect-error
                        onChange={setShouldSyncOldEmails}
                    >
                        <Radio value='1'>
                            <Body>
                                Yes, add emails from the last {INITIAL_SYNC_NUM_DAYS_AGO} days
                            </Body>
                        </Radio>
                        <Radio value='0'>
                            <Body>No, don’t pull in any of my existing emails</Body>
                        </Radio>
                    </RadioGroup>
                </SpacerGroup>
                <DisabledSection disabled={disableCreate} heightFitContent={true}>
                    <ModalTitle>
                        Would you like us to add people to Nutshell from the people that you
                        recently emailed?
                    </ModalTitle>
                    <RadioGroup
                        name='create-people'
                        selectedValue={disableCreate ? '0' : shouldCreatePeople}
                        // @ts-expect-error
                        onChange={setShouldCreatePeople}
                    >
                        <Radio value='1' disabled={disableCreate}>
                            <Body>
                                Yes, please add people I’ve emailed in the past{' '}
                                {INITIAL_SYNC_NUM_DAYS_AGO} days
                            </Body>
                        </Radio>
                        <Radio value='0' disabled={disableCreate}>
                            <Body>No, I’ll add people myself</Body>
                        </Radio>
                    </RadioGroup>
                </DisabledSection>
            </ModalMain>
            <FooterWithControls
                controls={<SlideshowControls selectedSlide={1} slideCount={3} />}
                buttons={
                    <React.Fragment>
                        <Button variant='text' onClick={props.goBack}>
                            Back
                        </Button>
                        <Link
                            variant='button-primary'
                            size='big'
                            // @ts-expect-error
                            as={RoutedLink}
                            to={`${props.nextPage}&o=${shouldSyncOldEmails}&c=${
                                disableCreate ? '0' : shouldCreatePeople
                            }`}
                        >
                            Next
                        </Link>
                    </React.Fragment>
                }
            />
        </RightFrame>
    );
}
